// pages/BigBoardModif.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  TablePagination,
  Slider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  VisibilityOff,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Straighten as StraightenIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  RemoveRedEye as MonitorIcon
} from '@mui/icons-material'; 
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { useBigBoardTable } from '../BigBoardTableContext';
import BigBoardFilters from '../components/BigBoardFilters';
import { canUseProspectFilter } from '../utils/prospectUtils';
import { matchesPlayerType } from '../utils/playerTypes';
import FlagDisplay from '../components/FlagDisplay';

/**
 * Fonction utilitaire pour gérer la couleur de texte
 * en fonction de la couleur de fond (hex).
 */
const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/* Helper components from BigBoard */
const FormaIndicator = ({ forma }) => {
  if (!forma) return null;

  switch (forma?.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    default:
      return null;
  }
};

const calculateDecimalAge = (anneeNaissance) => {
  if (!anneeNaissance) return '';
  const currentYear = new Date().getFullYear();
  const age = currentYear - anneeNaissance;
  return age;
};

const VerdictIndicator = ({ player }) => {
  if (!player.scout_verdicts || player.scout_verdicts.length === 0) return null;

  // Group verdicts by type
  const verdictGroups = player.scout_verdicts.reduce((acc, verdict) => {
    if (!acc[verdict.verdict]) {
      acc[verdict.verdict] = [];
    }
    acc[verdict.verdict].push(verdict);
    return acc;
  }, {});

  const verdictConfig = {
    'VOLVER A VER': { emoji: '🔄', color: '#2196f3' },
    'DESCARTADO': { emoji: '❌', color: '#f44336' },
    'MONITORIZAR': { emoji: '👁️', color: '#ff9800' },
    'PRIORITARIO': { emoji: '⭐', color: '#4caf50' },
    'DUDAS': { emoji: '❓', color: '#9c27b0' }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {Object.entries(verdictGroups).map(([verdictType, verdicts]) => {
        const config = verdictConfig[verdictType];
        if (!config) return null;

        const count = verdicts.length;

        return (
          <Box key={verdictType} sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s'
                }
              }}
            >
              {config.emoji}
            </Box>
            {count > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: config.color,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 'bold',
                  border: '1px solid #fff',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const CountBadge = ({ count }) => {
  const backgroundColor = count <= 1 ? '#f44336' : '#4caf50';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        color: '#ffffff',
        width: '28px',
        height: '28px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        border: '2px solid #fff',
      }}
    >
      {count}
    </Box>
  );
};

const badgeStyle = (bgColor) => ({
  position: 'relative',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '14px',
  marginLeft: '-4px',
  border: '1px solid #fff',
});

const positionMappings = {
  Base: ['Base', 'Point Guard'],
  Escolta: ['Escolta', 'Shooting Guard', 'Guard'],
  Alerio: ['Alerio', 'Small Forward', 'Swingman'],
  'Ala-Pivot': ['Ala-Pivot', 'Ala-Pívot', 'Power Forward', 'Power Forward / Center'],
  Pivot: ['Pivot', 'Center']
};

/**
 * Ligne de joueur dans le tableau de gauche (disponibles).
 */
const DraggablePlayerRow = ({
  player,
  handlePlayerClick,
  bigBoardPlayers,
  setBigBoardPlayers,
  availablePlayers,
  setAvailablePlayers,
  setVisibleThreshold,
  mediaUrl
}) => {
  // Drag setup
  const [{ isDragging }, dragRef] = useDrag({
    type: 'player',
    item: player,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  // Vérifie si le joueur est déjà dans le Big Board
  const isInBigBoard = bigBoardPlayers.some((p) => p.id === player.id);

  /**
   * Ajoute le joueur au Big Board (si pas déjà présent).
   */
  const movePlayerToBigBoard = (playerToMove) => {
    if (!isInBigBoard) {
      // On incrémente le classement de ceux déjà dans le board
      const updatedExisting = bigBoardPlayers.map((p) => ({
        ...p,
        classement: p.classement + 1
      }));
      // Nouveau joueur avec classement=1
      const newPlayer = { ...playerToMove, classement: 1 };
      setBigBoardPlayers([...updatedExisting, newPlayer]);
      // Retirer de la liste de gauche
      setAvailablePlayers((prev) => prev.filter((pl) => pl.id !== playerToMove.id));
      // Le nouveau joueur est visible par défaut
      setVisibleThreshold((prev) => prev + 1);
    }
  };

  /**
   * Retire le joueur du Big Board et le remet dans la liste de gauche.
   */
  const removePlayerFromBigBoard = (playerToRemove) => {
    const filtered = bigBoardPlayers.filter((p) => p.id !== playerToRemove.id);
    // Réajuster les classements
    const updated = filtered.map((p, index) => ({
      ...p,
      classement: filtered.length - index
    }));
    setBigBoardPlayers(updated);

    // Réinsère dans la liste de gauche, trié par nom
    setAvailablePlayers((prev) =>
      [...prev, playerToRemove].sort((a, b) => a.nom.localeCompare(b.nom))
    );
    // Ajuster le seuil de visibilité
    setVisibleThreshold((prev) => Math.max(1, prev - 1));
  };

  /**
   * Checkbox : coche => ajout, décoche => retrait
   */
  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      movePlayerToBigBoard(player);
    } else {
      removePlayerFromBigBoard(player);
    }
  };

  return (
    <TableRow
      ref={dragRef}
      key={player.id}
      hover
      sx={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
    >
      <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
        <Checkbox checked={isInBigBoard} onChange={handleCheckboxChange} />
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagDisplay nationality={player.nationalite} />
          <Avatar
            src={`${mediaUrl}/photos/${player.id}.png`}
            alt={player.nom}
            sx={{ width: 36, height: 36 }}
          />
          <VerdictIndicator player={player} />
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="a"
            onClick={(e) => {
              e.preventDefault();
              handlePlayerClick(player);
            }}
            sx={{
              color: 'black',
              textDecoration: 'none',
              fontSize: '14px',
              '&:hover': {
                textDecoration: 'underline',
                cursor: 'pointer'
              }
            }}
          >
            {player.nom}
          </Box>
          {player.is_in_injury === 1 && (
            <HealingIcon sx={{ color: 'red', fontSize: 16 }} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        <FormaIndicator forma={player.forme_indicator} />
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.poste}
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.annee_naissance}
        {player.annee_naissance && ` (${calculateDecimalAge(player.annee_naissance)})`}
      </TableCell>
      <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
        {player.taille}cm
      </TableCell>
      <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
        <Box>
          <div>{player.nationalite && player.nationalite !== 'null' ? player.nationalite : ''}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {player.passport && player.passport !== 'null' ? player.passport : ''}
          </div>
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
        <Box>
          <div>{player.team}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{player.league_1}</div>
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
        {player.groupes && player.groupes.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {player.groupes.slice(0, 2).map((group, idx) => (
              <Chip
                key={idx}
                label={group.libelle}
                size="small"
                sx={{
                  backgroundColor: group.code_couleur || '#grey',
                  color: getContrastColor(group.code_couleur),
                  fontSize: '11px',
                  height: '20px'
                }}
              />
            ))}
            {player.groupes.length > 2 && (
              <Chip
                label={`+${player.groupes.length - 2}`}
                size="small"
                sx={{ fontSize: '11px', height: '20px' }}
              />
            )}
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <CountBadge count={player.nb_informe || 0} />
          <CountBadge count={player.nb_scout || 0} />
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
          {player.is_in_big_board === 1 && (
            <Box sx={badgeStyle('#1976d2')}>
              <StarIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_follow_list === 1 && (
            <Box sx={badgeStyle('#dc004e')}>
              <VisibilityIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_informe === 1 && (
            <Box sx={badgeStyle('#4caf50')}>
              <AssignmentIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_intel === 1 && (
            <Box sx={badgeStyle('#ff9800')}>
              <LightbulbIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_fiche_technique === 1 && (
            <Box sx={badgeStyle('#2196f3')}>
              <DescriptionIcon fontSize="inherit" />
            </Box>
          )}
          {player.is_in_video === 1 && (
            <Box sx={badgeStyle('#9c27b0')}>
              <VideoLibraryIcon fontSize="inherit" />
            </Box>
          )}
          {player.has_medidas === 1 && (
            <Box sx={badgeStyle('#00bcd4')}>
              <StraightenIcon fontSize="inherit" />
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const BigBoardModif = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;
  const userData = JSON.parse(localStorage.getItem('user'));
  const userRole = userData?.role?.toLowerCase() || '';
  // L'exclusion du vivier « NCAA + » sous PRO ne vaut que pour qui voit le bucket.
  const canSeeProspects = canUseProspectFilter(userRole);

  const { tags, refreshData } = useBigBoardTable();

  // Loading / Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Nom du BigBoard, type, tags sélectionnés
  const [bigBoardName, setBigBoardName] = useState('');
  const [bigBoardType, setBigBoardType] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  // Joueurs du BigBoard (droite) + joueurs dispo (gauche) + tous (filtrés par type)
  const [bigBoardPlayers, setBigBoardPlayers] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);

  // États pour les groupes
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // Recherche fusionnée nom+équipe
  const [searchFilter, setSearchFilter] = useState('');

  // Autres filtres
  const [positionFilter, setPositionFilter] = useState([]);
  const [heightFilter, setHeightFilter] = useState([100, 230]);
  const [ageFilter, setAgeFilter] = useState([8, 42]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [uniqueLeagues, setUniqueLeagues] = useState([]);

  // New filter states from BigBoard
  const [nameFilter, setNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [playerTypeFilter, setPlayerTypeFilter] = useState(0);
  const [informeFilter, setInformeFilter] = useState(null);
  const [scoutFilter, setScoutFilter] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState([]);
  const [groupColors, setGroupColors] = useState({});

  // --------------------------------------------------
  // Nouveau state pour la coupure de visibilité
  // --------------------------------------------------
  const [visibleThreshold, setVisibleThreshold] = useState(1);

  // ------------------------------------------------
  // Helpers : un joueur est-il visible ?
  // ------------------------------------------------
  const isVisible = (player) => player.classement <= visibleThreshold;

  /**
   * Récupération du Big Board existant, puis récupération de tous les joueurs filtrés par type.
   */
  useEffect(() => {
    const fetchBigBoard = async () => {
      try {
        setLoading(true);

        // 1) Charger le BigBoard
        const response = await api.get(`/api/bigboard/${id}`);
        const board = response.data;

        setBigBoardName(board.libelle || '');
        setSelectedTags(board.tags || []);

        // Type du board (Pro / Cantera), en minuscules
        const boardType = board.type ? board.type.toLowerCase() : '';
        setBigBoardType(boardType);

        // Joueurs déjà dans le bigboard, triés selon classement
        const sortedPlayers = board.players
          ? [...board.players].sort((a, b) => b.classement - a.classement)
          : [];
        setBigBoardPlayers(sortedPlayers);

        // ------------------------------------------------
        // seuil = nombre de joueurs visibles déjà existants
        // ------------------------------------------------
        const visibles = sortedPlayers.filter((p) => p.visible !== 'non').length;
        setVisibleThreshold(visibles || 1);

        // 2) Charger tous les joueurs
        const playersResponse = await api.get('/api/players');
        const allFetched = playersResponse.data.players || [];

        // 3) Filtrer par type
        const filteredByType = allFetched.filter((p) => {
          if (!p.type) return false;
          return p.type.toLowerCase() === boardType;
        });

        setAllPlayers(filteredByType);
        setError(null);
      } catch (err) {
        console.error('Error al recuperar el Ranking:', err);
        setError('No se puede cargar el Ranking.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBigBoard();
    }
  }, [id, backendUrl, section]);

  /**
   * Récupération de la liste des groupes (pour le filtre "Filtrar por grupos").
   */
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('/api/groups');
        setGroups(response.data);

        // Create color mapping for groups
        const colors = {};
        response.data.forEach(group => {
          colors[group.libelle] = group.code_couleur;
        });
        setGroupColors(colors);
      } catch (error) {
        console.error('Erreur lors de la récupération des groupes:', error);
      }
    };
    fetchGroups();
  }, [backendUrl, section]);

  /**
   * Met à jour la liste des joueurs disponibles et les ligues uniques
   * chaque fois que bigBoardPlayers ou allPlayers change.
   */
  useEffect(() => {
    const boardPlayerIds = bigBoardPlayers.map((p) => p.id);
    const available = allPlayers.filter((p) => !boardPlayerIds.includes(p.id));

    const leagues = Array.from(
      new Set(
        available
          .map((player) => player.league_1)
          .filter((league) => league && league.trim() !== '' && league.trim() !== 'null')
      )
    ).sort();

    setUniqueLeagues(leagues);
    setAvailablePlayers(available);
  }, [bigBoardPlayers, allPlayers]);

  /**
   * Filtrage des joueurs disponibles (colonne de gauche).
   */
  const filteredAvailablePlayers = availablePlayers.filter((player) => {
    const playerAge = new Date().getFullYear() - player.annee_naissance;

    // Name/team search (use nameFilter instead of searchFilter for consistency)
    const term = (nameFilter || searchFilter).toLowerCase();
    const matchesSearch =
      player.nom.toLowerCase().includes(term) ||
      (player.team && player.team.toLowerCase().includes(term));
    if (!matchesSearch) return false;

    // Year filter
    if (yearFilter && !player.annee_naissance.toString().includes(yearFilter))
      return false;

    // Type Pro / Cantera / NCAA + — règle unique : utils/playerTypes
    if (
      !matchesPlayerType(player, playerTypeFilter, { excludeProspects: canSeeProspects })
    ) {
      return false;
    }

    // Informe filter
    if (informeFilter) {
      if (informeFilter === 'zero') {
        // 0 informes: players with exactly 0 informe
        const count = player.nb_informe || 0;
        if (count !== 0) return false;
      } else if (informeFilter === 'red') {
        // 0-1 informes: players with 0 or 1 informe
        const count = player.nb_informe || 0;
        if (count > 1) return false;
      } else if (informeFilter === 'green') {
        // 2+ informes: players with 2 or more informes
        const count = player.nb_informe || 0;
        if (count <= 1) return false;
      }
    }

    // Scout filter
    if (scoutFilter) {
      if (scoutFilter === 'red') {
        // 0-1 scouts: players with 0 or 1 scout
        const count = player.nb_scout || 0;
        if (count > 1) return false;
      } else if (scoutFilter === 'green') {
        // 2+ scouts: players with 2 or more scouts
        const count = player.nb_scout || 0;
        if (count <= 1) return false;
      }
    }

    // Verdict filter
    if (verdictFilter.length > 0) {
      // Check if player has any of the selected verdicts
      if (!player.scout_verdicts || player.scout_verdicts.length === 0) {
        return false;
      }

      // Check if any of the player's verdicts match the selected filters
      const playerVerdicts = player.scout_verdicts.map(v => v.verdict);
      const hasMatchingVerdict = verdictFilter.some(v => playerVerdicts.includes(v));

      if (!hasMatchingVerdict) return false;
    }

    // Position filter
    if (positionFilter.length > 0) {
      const matchPosition = positionFilter.some((pos) =>
        positionMappings[pos].some(
          (mappedPos) => player.poste?.toLowerCase() === mappedPos.toLowerCase()
        )
      );
      if (!matchPosition) return false;
    }

    // Group filter
    if (groupFilter.length > 0) {
      if (groupFilter.includes('Sin Grupos')) {
        if (!player.groupes || player.groupes.length === 0) {
          // Player has no groups - matches "Sin Grupos"
        } else if (!groupFilter.some(g => g !== 'Sin Grupos')) {
          return false;
        }
      } else {
        const playerGroupNames = (player.groupes || []).map((g) => g.libelle);
        const hasOverlap = groupFilter.some((name) => playerGroupNames.includes(name));
        if (!hasOverlap) return false;
      }
    }

    // Height filter
    if (player.taille < heightFilter[0] || player.taille > heightFilter[1]) {
      return false;
    }

    // Age filter
    if (playerAge < ageFilter[0] || playerAge > ageFilter[1]) {
      return false;
    }

    return true;
  });

  /**
   * Enregistrement du BigBoard (PUT),
   * en prenant en compte la visibilité.
   */
  const handleSave = async () => {
    if (!bigBoardName.trim()) {
      setError('El nombre del bigboard no puede estar vacío');
      return;
    }

    const payloadPlayers = [...bigBoardPlayers]
      .reverse()
      .map((p) => ({
        id: p.id,
        visible: p.classement <= visibleThreshold ? '' : 'non'
      }));

    try {
      await api.put(`/api/bigboard/${id}`, {
        libelle: bigBoardName,
        players: payloadPlayers,
        tags: selectedTags.map((t) => t.id),
        type: bigBoardType === 'pro' ? 'Pro' : 'Cantera'
      });
      navigate(-1);
      setTimeout(refreshData, 100);
    } catch (err) {
      console.error('Error al actualizar el Ranking:', err);
      setError(err.response?.data?.message || 'Error al actualizar el Ranking.');
    }
  };

  /**
   * Clique sur le nom du joueur => fiche
   */
  const handlePlayerClick = (player) => {
    navigate(`/players/${player.id}`);
  };

  /**
   * Retirer un joueur du BigBoard
   */
  const removePlayerFromBigBoard = (player) => {
    const filtered = bigBoardPlayers.filter((p) => p.id !== player.id);
    const updated = filtered.map((p, index) => ({
      ...p,
      classement: filtered.length - index
    }));
    setBigBoardPlayers(updated);
    setAvailablePlayers((prev) =>
      [...prev, player].sort((a, b) => a.nom.localeCompare(b.nom))
    );
    setVisibleThreshold((prev) => Math.max(1, prev - 1));
  };

  /**
   * Ajouter un joueur dans le BigBoard au drop
   */
  const movePlayerToBigBoard = (player) => {
    const isAlreadyInBoard = bigBoardPlayers.some((p) => p.id === player.id);
    if (!isAlreadyInBoard) {
      const updated = bigBoardPlayers.map((p) => ({ ...p, classement: p.classement + 1 }));
      const newPlayer = { ...player, classement: 1 };
      setBigBoardPlayers([...updated, newPlayer]);
      setAvailablePlayers((prev) => prev.filter((p) => p.id !== player.id));
      setVisibleThreshold((prev) => prev + 1);
    }
  };

  /**
   * Réarrange l'ordre (drag interne).
   */
  const movePlayerInBigBoard = (dragIndex, hoverIndex) => {
    const newPlayers = [...bigBoardPlayers];
    const [movedPlayer] = newPlayers.splice(dragIndex, 1);
    newPlayers.splice(hoverIndex, 0, movedPlayer);

    const updated = newPlayers.map((p, index) => ({
      ...p,
      classement: newPlayers.length - index
    }));
    setBigBoardPlayers(updated);
  };

  /**
   * Joueur déjà dans le BigBoard, draggable pour réordonner.
   * Affiche l’œil barré si hors seuil de visibilité.
   */
  const DraggableBigBoardPlayer = ({ player, index }) => {
    const ref = useRef(null);

    const [, drag] = useDrag({
      type: 'bigboard-player',
      item: { index }
    });

    const [, drop] = useDrop({
      accept: 'bigboard-player',
      hover: (item) => {
        if (item.index !== index) {
          movePlayerInBigBoard(item.index, index);
          item.index = index;
        }
      }
    });

    drag(drop(ref));

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 0.5,
          mb: 0.5,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          cursor: 'move',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Typography sx={{ mr: 2, minWidth: '30px' }}>{player.classement}</Typography>
        <Avatar
          src={`${mediaUrl}/photos/${player.id}.png`}
          alt={player.nom}
          sx={{ mr: 2 }}
        />
        <Typography sx={{ flexGrow: 1 }}>{player.nom}</Typography>
        {!isVisible(player) && <VisibilityOff fontSize="small" sx={{ mr: 1 }} />}
        <IconButton onClick={() => removePlayerFromBigBoard(player)} color="error">
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  /**
   * Zone de drop du BigBoard
   */
  const BigBoardDropZone = () => {
    const [{ isOver }, dropRef] = useDrop({
      accept: 'player',
      drop: (player) => movePlayerToBigBoard(player),
      collect: (monitor) => ({ isOver: monitor.isOver() })
    });

    // Affiche à l'envers pour que classement=1 soit en bas
    const displayPlayers = [...bigBoardPlayers].reverse();

    return (
      <Paper
        ref={dropRef}
        sx={{
          p: 2,
          minHeight: '500px',
          backgroundColor: isOver ? 'rgba(255, 152, 0, 0.1)' : '#f5f5f5'
        }}
      >
        <Typography variant="h6" gutterBottom>
          Ranking
        </Typography>
        {displayPlayers.map((player, i) => (
          <DraggableBigBoardPlayer
            key={player.id}
            player={player}
            index={bigBoardPlayers.length - 1 - i}
          />
        ))}
        {bigBoardPlayers.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
            Arrastra jugador@s aquí
          </Typography>
        )}
      </Paper>
    );
  };

  // Affichage Loading / Erreur
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 3 }}>

        {/* CURSEUR DE VISIBILIDAD */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Visibilidad</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Slider
                value={visibleThreshold}
                min={1}
                max={Math.max(1, bigBoardPlayers.length)}
                step={1}
                marks
                onChange={(_, v) => setVisibleThreshold(v)}
                valueLabelDisplay="auto"
                sx={{ color: '#ff9800' }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                type="number"
                label="Top visibles"
                inputProps={{ min: 1, max: bigBoardPlayers.length }}
                value={visibleThreshold}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setVisibleThreshold(isNaN(val) ? 1 : val);
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Filtres */}
        <BigBoardFilters
          playerTypeFilter={playerTypeFilter}
          setPlayerTypeFilter={setPlayerTypeFilter}
          informeFilter={informeFilter}
          setInformeFilter={setInformeFilter}
          scoutFilter={scoutFilter}
          setScoutFilter={setScoutFilter}
          verdictFilter={verdictFilter}
          setVerdictFilter={setVerdictFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          yearFilter={yearFilter}
          setYearFilter={setYearFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          groupFilter={groupFilter}
          setGroupFilter={setGroupFilter}
          heightFilter={heightFilter}
          setHeightFilter={setHeightFilter}
          ageFilter={ageFilter}
          setAgeFilter={setAgeFilter}
          groups={groups}
          groupColors={groupColors}
          userRole={userRole}
        />

        {/* Configuration du Big Board (Nom + Tags) */}
        <Box sx={{ mb: 6, mt: 6 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Nombre del Ranking"
                value={bigBoardName}
                onChange={(e) => setBigBoardName(e.target.value)}
                error={!bigBoardName.trim()}
                helperText={!bigBoardName.trim() ? 'El nombre es obligatorio' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={selectedTags}
                  onChange={(e) => setSelectedTags(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.libelle}
                          style={{
                            backgroundColor: tag.code_couleur,
                            color: getContrastColor(tag.code_couleur)
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {tags.map((tag) => (
                    <MenuItem key={tag.id} value={tag}>
                      <Checkbox checked={selectedTags.some((t) => t.id === tag.id)} />
                      <ListItemText primary={tag.libelle} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCELAR
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              GUARDAR CAMBIOS
            </Button>
          </Box>
        </Box>

        {/* Tableau de gauche (joueurs) + dropzone (droite) */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#F25F29',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '50px',
                        padding: '8px 4px'
                      }}
                    >
                      <Checkbox
                        disabled
                        sx={{ color: 'white' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'inline-block'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{filteredAvailablePlayers.length}</span>
                        <span style={{ fontSize: '9px', opacity: 0.8, marginLeft: '2px' }}>Jugador@s</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '120px', padding: '8px 4px' }}>
                      Nombre
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      Forma
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      Posición
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '100px', padding: '8px 4px' }}>
                      Año
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      Altura
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '100px', padding: '8px 4px' }}>
                      Nacionalidad
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      Equipo
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      Grupos
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                      Informe/Scout
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '140px', padding: '8px 4px' }}>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAvailablePlayers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((player) => (
                      <DraggablePlayerRow
                        key={player.id}
                        player={player}
                        handlePlayerClick={handlePlayerClick}
                        bigBoardPlayers={bigBoardPlayers}
                        setBigBoardPlayers={setBigBoardPlayers}
                        availablePlayers={availablePlayers}
                        setAvailablePlayers={setAvailablePlayers}
                        setVisibleThreshold={setVisibleThreshold}
                        mediaUrl={mediaUrl}
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAvailablePlayers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              rowsPerPageOptions={[10]}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <BigBoardDropZone />
          </Grid>
        </Grid>
      </Box>
    </DndProvider>
  );
};

export default BigBoardModif;
